<template>
    <div>
        <h3>Associated TMLNs</h3>
        <confirm-dialog ref="confirmRemoveAssociation"></confirm-dialog>
        <confirm-dialog ref="confirmRemoveGroup"></confirm-dialog>
        <div v-if="errorMessageRemove" class="alert alert-danger"> {{ errorMessageRemove }}</div>
        <div v-if="isLicenceEditor" class="btn-group mt-2">
            <button class="btn btn-primary" style="height: 38px" @click="createAssociation">
                New Association
            </button>
            <button v-if="model && model.length" class="btn btn-secondary" style="height: 38px" @click="associateTmln">
                Associate TMLN
            </button>
        </div>
        <Modal ref="newAssociationModal" :title="groupLabel" :disabled="false" :onSave="save" :saveText="saveLabel"
            :automaticClose="false">
            <template #form>
                <div class="row">
                    <div class="col-12">
                        <smart-input v-model="groupName" id="groupName" label="Group Name *"
                            :is-required="!!v$.groupName.$required" :validator="v$.groupName" />
                    </div>
                </div>
                <div class="row">
                    <div class="col-12">
                        <smart-select id="groupType" v-model="groupType" label="Group Type *" :options="groupTypeSelect"
                            :validator="v$.groupType" :is-required="!!v$.groupType.$required" />
                    </div>
                </div>
                <div v-if="errorMessage" class="alert alert-danger"> {{ errorMessage }}</div>
            </template>
        </Modal>
        <Modal ref="associateTmlnModal" title="Associate TMLN" :disabled="false" :onSave="saveAssociation"
            saveText="Associate TMLN" :automaticClose="false">
            <template #form>
                <div class="row">
                    <div class="col">
                        <p>Enter a TMLN and select a Group to associate the TMLN to an association with this licence.
                        </p>
                    </div>
                </div>
                <div class="row">
                    <div class="col-6">
                        <smart-input @input="errorMessage = null" v-model="tmlnNumber" id="tmlnNumber"
                            label="TMLN Number *" :is-required="!!v$.tmlnNumber.$required" :validator="v$.tmlnNumber" />
                    </div>
                </div>
                <div class="row">
                    <div class="col-6">
                        <smart-select-key-value id="groups" v-model="groupId" label="Groups *" :options="selectGroup"
                            :validator="v$.groupId" :is-required="!!v$.groupId.$required" />
                    </div>
                </div>
                <div v-if="errorMessage" class="alert alert-danger"> {{ errorMessage }}</div>
            </template>
        </Modal>
    </div>
    <card v-for="group in model" :key="group.trustmarkBusinessGroupId" class="mt-4">
        <div class="w-100">
            <table v-if="model && model.length > 0" class="table table-hover">
                <thead>
                    <tr>
                        <th colspan="5" class="fw-normal">
                            <div class="d-flex justify-content-between">
                                <div>
                                    {{ group.groupName }} /
                                    {{ group.groupType }} <span v-if="group.associations"
                                        style="height: 25px; width: 25px; background-color: #71217B;"
                                        class="d-inline-flex justify-content-center align-items-center rounded-circle text-light">
                                        {{ group.associations.length }}</span>
                                </div>
                                <div class="btn-group btn-group">
                                    <button v-if="isLicenceGroupRemover"
                                        @click="removeGroup(group.trustmarkBusinessGroupId)"
                                        class="btn btn-outline-secondary">
                                        Remove Group
                                    </button>
                                    <button @click="editGroup(group)" class="btn btn-secondary">
                                        Edit Group
                                    </button>
                                </div>
                            </div>
                        </th>
                    </tr>
                    <tr>
                        <th scope="col">Registered Business Name</th>
                        <th scope="col">TMLN</th>
                        <th scope="col">Included</th>
                        <th scope="col">Last Action Date</th>
                        <th></th>
                    </tr>
                </thead>
                <tbody>
                    <tr v-for="association in group.associations" :key="association.tmln">
                        <td>{{ association.registeredCompanyName }}</td>
                        <td>
                            <router-link :to="`/tmlns/${association.trustmarkBusinessId}`">
                                {{ association.tmln }}
                            </router-link>
                        </td>
                        <td>{{ association.isIncluded ? 'Yes' : 'No' }}</td>
                        <td>{{ $helpers.dateString(association.lastActionDate) }}</td>
                        <td v-if="isLicenceEditor">
                            <div class="btn-group btn-group">
                                <button class="btn btn-outline-secondary"
                                    @click="removeAssociation(group.trustmarkBusinessGroupId, association.trustmarkBusinessId)"
                                    :disabled="!isLicenceEditor || !association.isIncluded">
                                    Remove Association
                                </button>
                            </div>
                        </td>
                    </tr>
                </tbody>
            </table>
        </div>
    </card>
</template>

<script>
import useValidate from '@vuelidate/core';
import { helpers, numeric, required } from '@vuelidate/validators';
import Modal from '../../Modal.vue';
import { SmartInput, SmartSelect, Card, SmartSelectKeyValue, ConfirmDialog, confirmType } from '@trustmark-2005-ltd/trustmark-shared-components';

export default {
    name: 'LicenceNumberViewAssociatedTmlns',
    components: {
        Modal,
        SmartInput,
        SmartSelect,
        SmartSelectKeyValue,
        Card,
        ConfirmDialog
    },
    data() {
        return {
            v$: useValidate(),
            groupName: '',
            groupType: '',
            loading: false,
            errorMessage: null,
            errorMessageRemove: null,
            tmlnNumber: '',
            groupId: '',
            isEditGroup: null,
        };
    },
    computed: {
        groupTypeSelect() {
            return ['Organisation A', 'Organisation B', 'Organisation C'];
        },
        selectGroup() {
            return this.model
                .map(group => {
                    return {
                        key: group.trustmarkBusinessGroupId,
                        value: group.groupName
                    }
                });
        },
        currentAccount() {
            return this.$store.state.auth.account;
        },
        isLicenceEditor() {
            return this.currentAccount && this.currentAccount.roles
                ? this.currentAccount.roles.includes('LicenceEditor')
                : false;
        },
        isLicenceGroupRemover() {
            return this.currentAccount && this.currentAccount.roles
                ? this.currentAccount.roles.includes('LicenceGroupRemover')
                : false;
        },
        groupedByGroupId() {
            const associationList = this.$store.state.licenceNumber.trustmarkBusinessGroupAssociations;
            return associationList.reduce((groupAssociation, association) => {
                const groupId = association.trustmarkBusinessGroupId;
                if (!groupAssociation[groupId]) {
                    groupAssociation[groupId] = [];
                }
                groupAssociation[groupId].push(association);
                return groupAssociation;
            }, {});
        },
        model() {
            return Object.entries(this.groupedByGroupId).map(([trustmarkBusinessGroupId, associations]) => {
                return {
                    trustmarkBusinessGroupId,
                    groupName: associations[0].groupName,
                    groupType: associations[0].groupType,
                    associations
                }
            })
        },
        saveLabel() {
            return this.isEditGroup ? 'Update' : 'Save';
        },
        groupLabel() {
            return this.isEditGroup ? 'Update Group' : 'New Association';
        },
    },
    async mounted() {
        window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
        const payload = {
            trustmarkBusinessId: this.$route.params.tmBusinessId,
        };
        await this.$store.dispatch('licenceNumber/getAllAssociationsByTrustmarkBusinessId', payload);
    },
    methods: {
        createAssociation() {
            this.groupName = '';
            this.groupType = '';
            this.$refs.newAssociationModal.show();
        },
        associateTmln() {
            this.$refs.associateTmlnModal.show();
        },
        async save() {
            this.v$.groupName.$validate();
            this.v$.groupType.$validate();
            if (this.v$.groupName.$invalid || this.v$.groupType.$invalid) {
                window.scrollTo(0, 0);
                return;
            }
            if (this.isEditGroup) {
                const payload = {
                    trustmarkBusinessGroupId: this.isEditGroup.trustmarkBusinessGroupId,
                    groupName: this.groupName,
                    groupType: this.groupType
                }
                this.$store.dispatch('licenceNumber/updateGroup', payload)
                    .then(() => {
                        window.scrollTo(0, 0);
                        this.$refs.newAssociationModal.hide();
                        this.$store.dispatch('licenceNumber/getAllAssociationsByTrustmarkBusinessId', { trustmarkBusinessId: this.$route.params.tmBusinessId });
                    }).catch((err) => {
                        window.scrollTo(0, 0);
                        this.loading = false;
                        this.errorMessage =
                            (err.response && err.response.data && err.response.data.message) ||
                            err.message ||
                            err.toString();
                    });
            } else {
                const payload = {
                    trustmarkBusinessId: this.$route.params.tmBusinessId,
                    trustmarkBusinessGroup: {
                        groupName: this.groupName,
                        groupType: this.groupType
                    }
                }
                this.$store.dispatch('licenceNumber/createAssociation', payload)
                    .then(() => {
                        window.scrollTo(0, 0);
                        this.$refs.newAssociationModal.hide();
                        this.$store.dispatch('licenceNumber/getAllAssociationsByTrustmarkBusinessId', { trustmarkBusinessId: payload.trustmarkBusinessId });
                    }).catch((err) => {
                        window.scrollTo(0, 0);
                        this.loading = false;
                        this.errorMessage =
                            (err.response && err.response.data && err.response.data.message) ||
                            err.message ||
                            err.toString();
                    });
            }
        },
        isTmlnMatch() {
            if (this.model) {
                const groups = this.model.filter(
                    tmbgAssociation => tmbgAssociation.trustmarkBusinessGroupId === this.group
                );
                return groups
                    .flatMap(group => group.associations)
                    .some(association => association.tmln === this.tmlnNumber);
            }
        },
        async saveAssociation() {
            this.v$.tmlnNumber.$validate();
            this.v$.groupId.$validate();
            if (this.v$.groupId.$invalid || this.v$.tmlnNumber.$invalid) {
                window.scrollTo(0, 0);
                return;
            }
            if (this.isTmlnMatch()) {
                this.errorMessage = 'This TMLN is already associated with a group';
                window.scrollTo(0, 0);
                return;
            }
            const payload = {
                trustmarkBusinessGroupId: this.groupId,
                tmln: this.tmlnNumber
            }
            this.$store.dispatch('licenceNumber/joinAssociation', payload)
                .then(() => {
                    window.scrollTo(0, 0);
                    this.$refs.associateTmlnModal.hide();
                    this.$store.dispatch('licenceNumber/getAllAssociationsByTrustmarkBusinessId', { trustmarkBusinessId: this.$route.params.tmBusinessId });
                })
                .catch((err) => {
                    window.scrollTo(0, 0);
                    this.loading = false;
                    this.errorMessage =
                        (err.response && err.response.data && err.response.data.message) ||
                        err.message ||
                        err.toString();
                });
        },
        async removeAssociation(trustmarkBusinessGroupId, trustmarkBusinessId) {
            if (!trustmarkBusinessGroupId || !trustmarkBusinessId) {
                this.errorMessageRemove = 'Invalid trustmark business group/business id';
                window.scrollTo(0, 0);
                return;
            }
            const payload = { trustmarkBusinessGroupId, trustmarkBusinessId }
            this.$refs.confirmRemoveAssociation
                .show({
                    title: 'Remove Association',
                    message: 'Are you sure you want to remove association?',
                    confirmType: confirmType.standard,
                    okButtonText: 'Remove Association',
                })
                .then((result) => {
                    if (result === true) {
                        this.$store.dispatch('licenceNumber/removeAssociation', payload)
                            .then(() => {
                                this.errorMessageRemove = null;
                                window.scrollTo(0, 0);
                                this.$store.dispatch('licenceNumber/getAllAssociationsByTrustmarkBusinessId', { trustmarkBusinessId: this.$route.params.tmBusinessId });
                            })
                            .catch((err) => {
                                window.scrollTo(0, 0);
                                this.loading = false;
                                this.errorMessageRemove =
                                    (err.response && err.response.data && err.response.data.message) ||
                                    err.message ||
                                    err.toString();
                            });
                    }
                });
        },
        async removeGroup(trustmarkBusinessGroupId) {
            if (!trustmarkBusinessGroupId) {
                this.errorMessageRemove = 'Invalid trustmark business group id';
                window.scrollTo(0, 0);
                return;
            }
            const payload = { trustmarkBusinessGroupId }
            this.$refs.confirmRemoveGroup
                .show({
                    title: 'Remove Group',
                    message: 'Are you sure you want to remove this group? Please note that all associations linked to it will also be deleted',
                    confirmType: confirmType.standard,
                    okButtonText: 'Remove Group',
                })
                .then((result) => {
                    if (result === true) {
                        this.$store.dispatch('licenceNumber/deleteGroup', payload)
                            .then(() => {
                                this.errorMessageRemove = null;
                                window.scrollTo(0, 0);
                                this.$store.dispatch('licenceNumber/getAllAssociationsByTrustmarkBusinessId', { trustmarkBusinessId: this.$route.params.tmBusinessId });
                            })
                            .catch((err) => {
                                window.scrollTo(0, 0);
                                this.loading = false;
                                this.errorMessageRemove =
                                    (err.response && err.response.data && err.response.data.message) ||
                                    err.message ||
                                    err.toString();
                            });
                    }
                });
        },

        editGroup(group) {
            if (!group) {
                this.errorMessageRemove = 'Invalid trustmark business group association data';
                window.scrollTo(0, 0);
                return;
            }
            this.isEditGroup = group;
            this.groupName = group.groupName;
            this.groupType = group.groupType;
            this.$refs.newAssociationModal.show();
        }
    },
    validations() {
        return {
            groupName: { isRequired: helpers.withMessage('Group name is required', required) },
            groupType: { isRequired: helpers.withMessage('Group type is required', required) },
            tmlnNumber: { isRequired: helpers.withMessage('TMLN number is required', required), numeric },
            groupId: { isRequired: helpers.withMessage('Group selection is required', required) },
        };
    },
}
</script>
